import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  lazy,
  Suspense,
  memo,
} from "react";
import {
  getAllPartners,
  getAllEvents,
  getMyEvents,
  getAllEventSessionsTables,
} from "@/Services/EventServices";
import { getSessionTables } from "@/Services/EventMeetingServices";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  CheckCircle2,
  Sparkles,
  Globe2,
  Smartphone,
  Shuffle,
  Mic2,
  Calendar,
  MessageCircle,
  Crown,
  Clock,
  Users,
  Quote,
  Radio,
  ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import EventCard from "@/components/EventCard";
import TableCard from "@/components/TableCard";
import BrandLogo from "@/components/BrandLogo/BrandLogo";

// Lazy load heavy components
const EventRegisterModal = lazy(
  () => import("@/components/EventRegisterModal/EventRegisterModal"),
);
const LiveNowPanel = lazy(
  () => import("@/components/LiveNowPanel/LiveNowPanel"),
);
const TrendingCirclesStrip = lazy(
  () => import("@/components/TrendingCirclesStrip/TrendingCirclesStrip"),
);
const TodayTomorrow = lazy(() => import("@/components/TodayTomorrow"));

// ---------------------------------------------------------------------------
// CONSTANTS — defined outside component so they are never recreated
// ---------------------------------------------------------------------------
const DEFAULT_HERO_BG =
  "https://images.unsplash.com/photo-1707430393809-784967fe6fee?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGRhcmslMjBncmVlbiUyMHRlY2glMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc3ODcxNjQzOHww&ixlib=rb-4.1.0&q=85";

const ICON_MAP = {
  Globe2,
  Shuffle,
  Mic2,
  Calendar,
  MessageCircle,
  Crown,
  Users,
  Sparkles,
  Clock,
  Radio,
  Smartphone,
  ChevronDown,
};

const FALLBACK_TESTIMONIALS = [
  {
    quote:
      "This is the only room I cancel client calls for. The signal-to-noise is unreal.",
    name: "Sarah Khan",
    role: "Founder · Anyon AI",
  },
  {
    quote:
      "Met our Series A lead at a table I almost didn't show up for. vmeetio is shaping how I plan my week.",
    name: "Jordan T.",
    role: "Operator · Greentide",
  },
  {
    quote:
      "Better than any pitch event I've been to. Two real intros in 90 minutes, both went to second meetings.",
    name: "Dr. Imani Cole",
    role: "Researcher · DeepMind",
  },
];

const PRESS = [
  "TechCrunch",
  "The Information",
  "Sifted",
  "Fast Company",
  "Wired",
];

const FALLBACK_FAQ = [
  {
    q: "How is this different from a webinar or virtual conference?",
    a: "There's no stage to watch. Every member is at one of small 6–8 person tables having a real conversation. The agenda is a starter — your table can take it anywhere. You can leave a table at any time and join another, the way you'd move between conversations at a great dinner.",
  },
  {
    q: "Will I be on camera?",
    a: "Yes — vmeetio works because faces matter. You can dim your camera between sessions, but joining a table without it is like sitting at dinner with a paper bag on your head. The trade is worth it; the room is small and curated.",
  },
  {
    q: "What if I have to leave early?",
    a: 'Just leave. The table gets a brief, polite "It\'s been a pleasure and I look forward to connecting with you" message in your name, your seat opens for the waitlist, and the conversation continues. No awkward goodbyes.',
  },
  {
    q: "Who's actually in the room?",
    a: "Operators, founders, investors, designers, researchers and policy people. Every member is invited or vouched for. We share the Seeking & Giving signal for the room before doors open so you know if the trade is there.",
  },
  {
    q: "Do I need to be a member to attend?",
    a: "No. Anyone can register for a single event. Members get early access, reserved VIP table seats, and the post-event dossier. You can sponsor a seat for guests with a voucher code if you want to bring your team.",
  },
];

const FALLBACK_HOW_IT_WORKS = [
  {
    icon: "Globe2",
    title: "Join from web or app",
    body: "One link from your inbox. Works in the browser, in the iOS or Android app, or call-in audio.",
  },
  {
    icon: "Shuffle",
    title: "Move between tables",
    body: "Leave a table when the conversation runs its course. Pull up a chair at another in two taps.",
  },
  {
    icon: "Mic2",
    title: "Present to the whole room",
    body: "When the moderator opens the floor, the host or a guest speaker can address every table at once.",
  },
  {
    icon: "Calendar",
    title: "Scheduled networking windows",
    body: "Pre-event icebreakers, mid-event speed intros, and post-event coffee slots, all on the calendar.",
  },
  {
    icon: "MessageCircle",
    title: "Chat after the event",
    body: "Your table thread stays open for 14 days. Share files, follow up, and keep the conversation going.",
  },
  {
    icon: "Crown",
    title: "VIP tables for members",
    body: "vmeetio members get reserved seats at curated VIP tables capped at six guests.",
  },
];

const STATIC_CIRCLES = [
  {
    name: "Founders",
    count: 1284,
    blurb: "Operators building products, teams, and leverage.",
  },
  {
    name: "Investors",
    count: 412,
    blurb: "Pre-seed to growth — relationship-driven capital.",
  },
  {
    name: "Operators",
    count: 973,
    blurb: "GTM, product, design, and finance leaders.",
  },
  {
    name: "AI Research",
    count: 351,
    blurb: "Applied ML, inference, evals, and safety.",
  },
  {
    name: "Climate",
    count: 529,
    blurb: "Hardware, deployment, finance, and policy.",
  },
  {
    name: "Design",
    count: 467,
    blurb: "Design leaders shipping systems and taste.",
  },
];

const STATIC_MODERATORS = [
  {
    name: "Eliza Marston",
    title: "Partner · Operator-in-residence",
    avatar:
      "https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=400&q=75",
  },
  {
    name: "Dr. Imani Cole",
    title: "Researcher · AI Policy",
    avatar:
      "https://images.unsplash.com/photo-1524504388940-b1c1722653e1?auto=format&fit=crop&w=400&q=75",
  },
  {
    name: "Jordan T.",
    title: "Investor · Early-stage climate",
    avatar:
      "https://images.unsplash.com/photo-1547425260-76bcadfb4f2c?auto=format&fit=crop&w=400&q=75",
  },
  {
    name: "Hana A.",
    title: "Design Lead · Systems",
    avatar:
      "https://images.unsplash.com/photo-1551836022-d5d88e9218df?auto=format&fit=crop&w=400&q=75",
  },
];

const STATIC_SPONSORSHIPS = [
  {
    id: "s-ev-1",
    scope: "event",
    event_id: 2,
    company: "Greentide",
    logo: "",
    status: "approved",
  },
  {
    id: "s-t-1",
    scope: "table",
    table_id: 101,
    company: "Anyon AI",
    logo: "",
    status: "approved",
    sponsor_table_name: "Anyon AI · Inference Economics",
    sponsor_moderator_name: "Eliza Marston",
    welcome_video_url: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
  },
  {
    id: "s-t-2",
    scope: "table",
    table_id: 201,
    company: "SignalWorks",
    logo: "",
    status: "approved",
  },
];

const STATIC_CONTENT = {
  hero_image: DEFAULT_HERO_BG,
  hero_overline: "A members-led society for virtual events",
  hero_headline_1: "Conversations",
  hero_headline_2: "worth showing up for.",
  hero_body:
    "vmeetio runs virtual events as a set of master sessions and small, curated tables. Move between topics. Build a trusted circle. Meet the people who can elevate your growth fastest.",
  hero_quote_text:
    "I came for one table. I left with two operator intros I would've spent a quarter trying to earn.",
  hero_quote_attribution: "Ava Moreno · Operator · Series B SaaS",
  value_strip: [
    {
      icon: "Users",
      title: "6–8 seat tables",
      body: "Small enough to remember names.",
    },
    {
      icon: "Shuffle",
      title: "Move freely",
      body: "Switch tables when the conversation shifts.",
    },
    {
      icon: "Sparkles",
      title: "Curated guests",
      body: "Vouched-for people with real trades to make.",
    },
  ],
  brands_overline: "Brands hosting events",
  brands_headline_1: "The brands convening",
  brands_headline_2: "this month's tables.",
  method_headline_1: "Master sessions.",
  method_headline_2: "Small tables.",
  method_headline_3: "Conversations worth showing up for.",
  method_body:
    "Every vmeetio event is structured as pre-set master sessions, with small tables of 6–8 people anchored by a moderator. You can leave a table at any time and join another — the same way you'd move between conversations at a great dinner.",
  circles_headline_1: "Find the people you'd actually",
  circles_headline_2: "email at midnight.",
  sponsor_tiers: [
    {
      id: "table",
      name: "Table Host",
      price: 8400,
      currency: "USD",
      cadence: "",
      tagline: "One curated table per event",
    },
    {
      id: "circle",
      name: "Circle Patron",
      price: 24000,
      currency: "USD",
      cadence: "",
      tagline: "Sponsor a full circle for a season",
    },
    {
      id: "founding",
      name: "Founding Partner",
      price: 0,
      currency: "",
      cadence: "Bespoke",
      tagline: "Year-round programming + co-branding",
    },
  ],
  testimonials: FALLBACK_TESTIMONIALS,
  faq: FALLBACK_FAQ,
  how_it_works: FALLBACK_HOW_IT_WORKS,
};

const SEEKING_GIVING_DATA = [
  {
    who: "Mei L.",
    role: "Founder · Anyon AI",
    seeking: "Series A introductions in EU climate-tech",
    giving: "Hard-won lessons on open-weight inference economics",
    topic: "AI After Hours",
  },
  {
    who: "Jordan T.",
    role: "Partner · North Capital",
    seeking: "Pre-seed climate hardware operators",
    giving: "Term-sheet templates, intro to LPs, board prep",
    topic: "Climate Capital Salon",
  },
  {
    who: "Hana A.",
    role: "Design lead · Linear Labs",
    seeking: "Brand designers who've shipped at scale",
    giving: "1:1 portfolio reviews, hiring intros",
    topic: "Designers, Off the Record",
  },
  {
    who: "Soren V.",
    role: "Founder · Greentide",
    seeking: "Co-founder on go-to-market",
    giving: "MRR-from-zero playbooks, channel partner intros",
    topic: "The Founders' Round",
  },
  {
    who: "Dr. Imani C.",
    role: "Researcher · DeepMind",
    seeking: "Policy operators on AI safety frameworks",
    giving: "Time on regulatory submissions, signal review",
    topic: "AI After Hours",
  },
  {
    who: "Ariel J.",
    role: "Operator · Stripe",
    seeking: "Founders scaling enterprise sales past $1M ARR",
    giving: "Enterprise GTM playbooks, candid feedback",
    topic: "The Founders' Round",
  },
];

const OUTCOME_STATS = [
  ["17", "founder–investor intros made last Thursday"],
  ["83%", "members got 1+ trade in their first event"],
  ["7 min", "average time-to-first-meaningful-conversation"],
  ["4.8★", "rating across 1,240 tables hosted"],
];

const METHOD_BLOCKS = [
  {
    n: "01",
    t: "Master sessions",
    d: "Pre-published agendas. Focus on the topics that matter, skip the rest.",
  },
  {
    n: "02",
    t: "Tables, not panels",
    d: "6–8 seats. A moderator. Everyone's voice fits in the room.",
  },
  {
    n: "03",
    t: "Move freely",
    d: "Switch tables when the conversation shifts. Your circle is the people you stayed with.",
  },
];

const MEMBERSHIP_TIERS = [
  ["Member", "Free · by introduction"],
  ["VIP", "$1,200 / yr"],
  ["Founding Circle", "By invitation"],
];

const DOSSIER_READING_LIST = [
  "· Bridge round dynamics, Q4 2025 — internal memo",
  "· The optionality premium — Marston, on Substack",
  "· Cap table hygiene at flat valuations — partner notes",
];

// ---------------------------------------------------------------------------
// PURE UTILITY FUNCTIONS — defined once, never recreated
// ---------------------------------------------------------------------------
const parseGmtOffsetMinutes = (tz) => {
  const m = String(tz || "").match(/GMT\s*([+-])\s*(\d{1,2})(?::(\d{2}))?/i);
  if (!m) return null;
  const sign = m[1] === "-" ? -1 : 1;
  const hh = parseInt(m[2], 10) || 0;
  const mm = parseInt(m[3] || "0", 10) || 0;
  return sign * (hh * 60 + mm);
};

const safeISO = (msOrDate) => {
  const d = new Date(msOrDate);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

const buildISOFromDateTime = ({ dateStr, timeStr, tzStr, isEnd }) => {
  if (!dateStr) return null;
  const t = (timeStr && String(timeStr).trim()) || (isEnd ? "23:59" : "00:00");
  const [Y, M, D] = String(dateStr)
    .split("-")
    .map((x) => parseInt(x, 10));
  const [hh, mm] = t.split(":").map((x) => parseInt(x, 10));
  const offsetMin = parseGmtOffsetMinutes(tzStr);
  if (offsetMin == null) return safeISO(`${dateStr}T${t}:00`);
  const utcMs = Date.UTC(Y, M - 1, D, hh, mm) - offsetMin * 60 * 1000;
  return safeISO(utcMs);
};

const normalizeEventData = (e) => ({
  id: e?._id,
  title: e?.eventName || "Untitled event",
  description: e?.description || "",
  cover: e?.eventImage || "",
  start_time: buildISOFromDateTime({
    dateStr: e?.startDate,
    timeStr: e?.startTime,
    tzStr: e?.timezone,
    isEnd: false,
  }),
  end_time: buildISOFromDateTime({
    dateStr: e?.endDate,
    timeStr: e?.endTime,
    tzStr: e?.timezone,
    isEnd: true,
  }),
  timezone: e?.timezone || "UTC",
  registered: Number(e?.bookingCount ?? 0),
  tags: [e?.meetingType, e?.visibility, e?.entry].filter(Boolean),
  sponcers: e?.sponcers || [],
  isBooked: false,
  actual_tables_count: 0,
  ...e,
});

const flattenAndNormalizeTables = (sessionDataArray) => {
  if (!Array.isArray(sessionDataArray)) return [];
  return sessionDataArray.flatMap((item) => {
    const sessionInfo = item.session;
    return (item.tables || []).map((t) => ({
      id: t._id,
      event_id: t.eventId,
      session_id: t.sessionId,
      session_name: sessionInfo?.name,
      circle: t?.sponsorDetails?.title || t?.sponcer?.title || "General",
      topic: t.tableName || `Table ${t.tableNumber}`,
      description: t.agenda || "",
      seats: Number(t.capacity || 8),
      taken: Number(t.currentOccupancy || 0),
      is_live: !!t.isActive,
      moderator_avatar: t?.tableIcon || t?.sponsorDetails?.image || "",
      welcome_video_url: t?.sponsorDetails?.introVideo || "",
      tableIcon: t?.sponsorDetails?.image || t?.tableIcon || "", 
      sponsorDetails: t?.sponsorDetails || null,
      __raw: t,
    }));
  });
};

const fmtTime = (isoStr, tz) => {
  try {
    return new Date(isoStr).toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
      timeZone: tz,
    });
  } catch {
    return "";
  }
};

const getEventStatus = (e) => {
  const now = Date.now();
  const s = new Date(e.start_time).getTime();
  const en = new Date(e.end_time).getTime();
  if (now >= s && now < en) return { label: "Live now", tone: "live" };
  const diff = s - now;
  const hours = Math.floor(diff / 3600000);
  if (hours < 1) {
    const mins = Math.max(1, Math.floor(diff / 60000));
    return { label: `Starts in ${mins} min`, tone: "soon" };
  }
  if (hours < 24) return { label: `Starts in ${hours}h`, tone: "soon" };
  return { label: "Tomorrow", tone: "future" };
};

// ---------------------------------------------------------------------------
// SKELETON COMPONENTS — prevent CLS (Cumulative Layout Shift)
// ---------------------------------------------------------------------------
const SkeletonCard = memo(() => (
  <div className="rounded-3xl border border-white/[0.06] bg-white/[0.02] p-6 animate-pulse">
    <div className="h-48 rounded-xl bg-white/[0.06] mb-4" />
    <div className="h-4 rounded bg-white/[0.06] mb-2 w-3/4" />
    <div className="h-3 rounded bg-white/[0.04] w-1/2" />
  </div>
));
SkeletonCard.displayName = "SkeletonCard";

const SkeletonTableCard = memo(() => (
  <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5 animate-pulse">
    <div className="h-5 rounded bg-white/[0.06] mb-3 w-2/3" />
    <div className="h-3 rounded bg-white/[0.04] mb-2 w-full" />
    <div className="h-3 rounded bg-white/[0.04] w-4/5" />
  </div>
));
SkeletonTableCard.displayName = "SkeletonTableCard";

const SkeletonBrand = memo(() => (
  <div className="glass rounded-2xl p-5 flex flex-col items-center gap-3 animate-pulse">
    <div className="w-16 h-16 rounded-full bg-white/[0.06]" />
    <div className="h-3 rounded bg-white/[0.04] w-2/3" />
  </div>
));
SkeletonBrand.displayName = "SkeletonBrand";

// ---------------------------------------------------------------------------
// SMALL MEMOISED SUB-COMPONENTS — prevent full-page re-renders
// ---------------------------------------------------------------------------
const HeroValueStrip = memo(({ strip }) => (
  <div
    className="mt-8 pt-6 border-t border-white/[0.06] grid grid-cols-3 gap-6"
    data-testid="hero-value-strip"
  >
    {strip.map((v, i) => {
      const Icon = ICON_MAP[v.icon] || Users;
      return (
        <div key={i} data-testid={`hero-value-${i}`}>
          <Icon size={18} className="text-[#C6A87C] mb-3" />
          <p className="text-base text-white/95 font-medium leading-tight">
            {v.title}
          </p>
          <p className="text-sm text-white/65 mt-1.5 leading-snug">{v.body}</p>
        </div>
      );
    })}
  </div>
));
HeroValueStrip.displayName = "HeroValueStrip";

const HeroQuote = memo(({ text, attribution }) => (
  <figure
    className="mt-8 pl-6 border-l-2 border-[#C6A87C]/45 max-w-xl"
    data-testid="hero-quote"
  >
    <blockquote className="font-display italic text-xl md:text-2xl text-white/90 leading-snug tracking-tight">
      &ldquo;{text}&rdquo;
    </blockquote>
    <figcaption
      className="mt-3 text-sm text-white/65"
      data-testid="hero-quote-attr"
    >
      {(attribution || "").split("·").map((part, i, arr) => (
        <span key={i}>
          {i === 0 ? (
            <span className="text-[#C6A87C]">{part.trim()}</span>
          ) : (
            part.trim()
          )}
          {i < arr.length - 1 ? " · " : ""}
        </span>
      ))}
    </figcaption>
  </figure>
));
HeroQuote.displayName = "HeroQuote";

const HeroTrustedStrip = memo(({ brands }) => {
  if (!brands.length) return null;
  return (
    <div
      className="mt-10 pt-6 border-t border-white/[0.06]"
      data-testid="hero-trusted-strip"
    >
      <p className="overline mb-4 text-[11px]">
        Members of these companies vmeetio here
      </p>
      <div className="flex flex-wrap items-center gap-x-7 gap-y-4">
        {brands.slice(0, 6).map((b) => (
          <Link
            key={b._id}
            to={`/brands/${encodeURIComponent(b.title)}`}
            state={{ sponsorData: b }}
            data-testid={`hero-trusted-${b.title}`}
            className="flex items-center gap-2.5 opacity-75 hover:opacity-100 transition-opacity"
          >
            <BrandLogo company={b.title} logo={b.image} size={30} />
            <span className="text-base text-white/90 hover:text-[#C6A87C] transition-colors font-medium">
              {b.title}
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
});
HeroTrustedStrip.displayName = "HeroTrustedStrip";

const OutcomeStrip = memo(() => (
  <div
    className="mt-12 md:mt-14 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10 border-t border-white/[0.08] pt-7"
    data-testid="hero-outcome-strip"
  >
    {OUTCOME_STATS.map(([n, l], i) => (
      <div key={i} data-testid={`outcome-${i}`}>
        <div className="font-display text-3xl md:text-4xl tracking-tighter text-[#C6A87C]">
          {n}
        </div>
        <div className="text-xs text-white/75 mt-2 leading-relaxed">{l}</div>
      </div>
    ))}
  </div>
));
OutcomeStrip.displayName = "OutcomeStrip";

const HeroLiveScoreboard = memo(
  ({ tables, circles, loadingTables, liveTableCount }) => (
    <Link
      to="/lobby"
      data-testid="hero-side-cta"
      className="group block rounded-2xl border border-[#C6A87C]/45 bg-[#C6A87C]/[0.08] hover:border-[#C6A87C] hover:bg-[#C6A87C]/[0.14] transition-colors p-5 md:p-6 mb-4"
    >
      <div className="flex items-center gap-3 mb-2.5">
        <span className="w-2.5 h-2.5 rounded-full bg-[#7BC8A6] ring-live shrink-0" />
        <p className="overline text-[#7BC8A6] text-[11px]">Live right now</p>
      </div>
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <p className="font-display tracking-tight leading-none">
            <span className="text-5xl md:text-6xl text-[#C6A87C]">
              {loadingTables ? "..." : tables.length}
            </span>
            <span className="text-2xl md:text-3xl text-white/90 ml-2">
              tables live
            </span>
          </p>
          <p className="text-sm md:text-base text-white/65 mt-2">
            Across {loadingTables ? "..." : Math.max(1, liveTableCount)} circles
            · {tables.reduce((sum, t) => sum + Number(t.taken || 0), 0)} members
            seated
          </p>
        </div>
        <span className="shrink-0 inline-flex items-center gap-2 text-sm md:text-base text-[#C6A87C] group-hover:text-[#D9BC93] font-medium">
          Browse all
          <ArrowRight
            size={16}
            className="group-hover:translate-x-0.5 transition-transform"
          />
        </span>
      </div>
    </Link>
  ),
);
HeroLiveScoreboard.displayName = "HeroLiveScoreboard";

const TestimonialSection = memo(
  ({ testimonials, activeQuote, setActiveQuote }) => (
    <section
      className="border-b border-white/[0.06]"
      data-testid="testimonial-section"
      style={{
        background: "linear-gradient(180deg, #061412 0%, #0A1D1A 100%)",
      }}
    >
      <div className="container-x py-14 md:py-16">
        <div className="grid md:grid-cols-12 gap-10 items-start">
          <div
            className="md:col-span-7 relative"
            data-testid="testimonial-card"
          >
            <Quote className="text-[#C6A87C]/60 mb-4" size={28} />
            <blockquote className="font-display text-2xl md:text-3xl tracking-tight leading-snug">
              "{testimonials[activeQuote].quote}"
            </blockquote>
            <p className="mt-5 text-sm">
              <span className="text-white/85">
                {testimonials[activeQuote].name}
              </span>
              <span className="text-white/55">
                {" "}
                · {testimonials[activeQuote].role}
              </span>
            </p>
            <div
              className="mt-6 flex items-center gap-2"
              data-testid="testimonial-dots"
            >
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActiveQuote(i)}
                  aria-label={`Show testimonial ${i + 1}`}
                  data-testid={`testimonial-dot-${i}`}
                  className={`h-1.5 rounded-full transition-all ${
                    i === activeQuote
                      ? "w-6 bg-[#C6A87C]"
                      : "w-1.5 bg-white/20 hover:bg-white/40"
                  }`}
                />
              ))}
            </div>
          </div>
          <div className="md:col-span-5" data-testid="press-strip">
            <p className="overline mb-4">Covered in</p>
            <div className="flex flex-wrap gap-x-6 gap-y-3 items-center opacity-70">
              {PRESS.map((p) => (
                <span
                  key={p}
                  data-testid={`press-${p.replace(/\s+/g, "-")}`}
                  className="font-display text-lg italic text-white/85"
                >
                  {p}
                </span>
              ))}
            </div>
            <p className="text-[11px] uppercase tracking-wider text-white/45 mt-4">
              Placeholder — replace with real coverage
            </p>
          </div>
        </div>
      </div>
    </section>
  ),
);
TestimonialSection.displayName = "TestimonialSection";

const BrandsSection = memo(({ brands, loadingBrands, cms }) => (
  <section className="section" data-testid="trusted-by-section">
    <div className="container-x">
      <div className="grid md:grid-cols-12 gap-10 mb-12 md:mb-14 items-end">
        <div className="md:col-span-7">
          <p className="overline mb-4">
            {cms.brands_overline || "Brands hosting events"}
          </p>
          <h2 className="font-display font-light text-4xl md:text-5xl tracking-tight leading-tight">
            {cms.brands_headline_1 || "The brands convening"}
            <br />
            <span className="italic text-[#C6A87C]">
              {cms.brands_headline_2 || "this month's tables."}
            </span>
          </h2>
        </div>
        <div className="md:col-span-5">
          <p className="text-white/75 leading-relaxed">
            Each brand brings a point of view to a table. Click any logo to see
            their overview, video and contact.
          </p>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-5 md:gap-6">
        {loadingBrands
          ? Array.from({ length: 5 }).map((_, i) => <SkeletonBrand key={i} />)
          : brands.map((b) => (
              <Link
                key={b._id}
                to={`/brands/${encodeURIComponent(b.title)}`}
                state={{ sponsorData: b }}
                data-testid={`trusted-${b.title}`}
                className="group glass rounded-2xl p-5 flex flex-col items-center text-center gap-3 transition-all duration-300 hover:-translate-y-1 hover:border-white/20"
              >
                <BrandLogo company={b.title} logo={b.image} size={64} />
                <span className="text-sm text-white/80 group-hover:text-[#C6A87C] transition-colors font-medium">
                  {b.title}
                </span>
                {b.descp && (
                  <span
                    className="text-xs text-white/45 leading-snug line-clamp-2"
                    dangerouslySetInnerHTML={{ __html: b.descp }}
                  />
                )}
              </Link>
            ))}
      </div>
    </div>
  </section>
));
BrandsSection.displayName = "BrandsSection";

const MethodSection = memo(({ cms }) => (
  <section className="section" data-testid="method-section">
    <div className="container-x">
      <div className="grid md:grid-cols-12 gap-12 mb-14">
        <div className="md:col-span-5">
          <p className="overline mb-5">The method</p>
          <h2 className="font-display font-light text-4xl md:text-5xl tracking-tight leading-tight">
            {cms.method_headline_1 || "Master sessions."}
            <br />
            {cms.method_headline_2 || "Small tables."}
            <br />
            {cms.method_headline_3 || "Conversations worth showing up for."}
          </h2>
        </div>
        <div className="md:col-span-6 md:col-start-7">
          <p className="text-white/75 text-lg leading-relaxed">
            {cms.method_body ||
              "Every vmeetio event is structured as pre-set master sessions, with small tables of 6–8 people anchored by a moderator. You can leave a table at any time and join another — the same way you'd move between conversations at a great dinner."}
          </p>
        </div>
      </div>
      <div className="grid md:grid-cols-3 gap-6">
        {METHOD_BLOCKS.map((b) => (
          <div
            key={b.n}
            className="glass rounded-2xl p-8 transition-all duration-300 hover:-translate-y-1 hover:border-white/20"
          >
            <div className="overline mb-6">{b.n}</div>
            <h3 className="font-display text-2xl tracking-tight mb-3">{b.t}</h3>
            <p className="text-white/70 text-sm leading-relaxed">{b.d}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
));
MethodSection.displayName = "MethodSection";

const HowItWorksSection = memo(({ steps }) => (
  <section
    className="section relative"
    data-testid="how-it-works-section"
    style={{ background: "linear-gradient(180deg, #0A1D1A 0%, #061412 100%)" }}
  >
    <div className="container-x">
      <div className="grid md:grid-cols-12 gap-12 mb-14 items-end">
        <div className="md:col-span-7">
          <p className="overline mb-4">Inside a vmeetio event</p>
          <h2 className="font-display font-light text-4xl md:text-5xl tracking-tight leading-tight">
            What it actually feels like to show up.
          </h2>
        </div>
        <div className="md:col-span-5">
          <p className="text-white/75 leading-relaxed">
            Whether you join from your phone on a train, your desk at home, or
            the room itself, you sit at the same table as everyone else.
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {steps.map((f, idx) => {
          const Icon =
            typeof f.icon === "string"
              ? ICON_MAP[f.icon] || Globe2
              : f.icon || Globe2;
          return (
            <div
              key={f.title || idx}
              data-testid={`how-${f.title || idx}`}
              className="glass rounded-2xl p-7 transition-all duration-300 hover:-translate-y-1 hover:border-white/20"
            >
              <Icon size={20} className="text-[#C6A87C] mb-5" />
              <h3 className="font-display text-xl tracking-tight mb-2">
                {f.title}
              </h3>
              <p className="text-sm text-white/60 leading-relaxed">{f.body}</p>
            </div>
          );
        })}
      </div>
    </div>
  </section>
));
HowItWorksSection.displayName = "HowItWorksSection";

// In LobbyPreviewSection
const LobbyPreviewSection = memo(
  ({ tables, events, loadingTables, sponsorsForTable }) => (
    <section
      className="section relative"
      data-testid="lobby-preview-section"
      style={{
        background: "linear-gradient(180deg, #061412 0%, #0A1D1A 100%)",
      }}
    >
      <div className="container-x">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12 gap-6">
          <div>
            <p className="overline mb-4">Live now · the lobby</p>
            <h2 className="font-display font-light text-4xl md:text-5xl tracking-tight">
              Pick a table. Pull up a chair.
            </h2>
          </div>
          <Link
            to="/lobby"
            className="btn-ghost text-sm self-start md:self-end"
            data-testid="lobby-preview-cta"
          >
            See all tables <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loadingTables ? (
            <>
              <SkeletonTableCard />
              <SkeletonTableCard />
              <SkeletonTableCard />
            </>
          ) : tables.length === 0 ? (
            <p className="text-white/50">No tables live at this moment.</p>
          ) : (
            tables.slice(0, 3).map((t) => {
              const tableEvent = events.find((e) => e.id === t.event_id);
              return (
                <TableCard
                  key={t.id}
                  table={t}
                  event={tableEvent || null}
                  sponsors={
                    t.sponsorDetails
                      ? [
                          {
                            id: t.sponsorDetails._id,
                            company: t.sponsorDetails.title,
                            logo: t.sponsorDetails.image,
                            image: t.sponsorDetails.image,
                            welcome_video_url: t.sponsorDetails.introVideo,
                            sponsorData: t.sponsorDetails,
                          },
                        ]
                      : []
                  }
                  onViewAgenda={() => {
                    console.log("View agenda for table:", t.id);
                  }}
                  onJoin={(data) => {
                    console.log("Join table:", data);
                  }}
                  onAttendeeClick={(name) => {
                    console.log("Attendee clicked:", name);
                  }}
                />
              );
            })
          )}
        </div>
      </div>
    </section>
  ),
);
LobbyPreviewSection.displayName = "LobbyPreviewSection";

const EventsSection = memo(
  ({ sortedEvents, loadingEvents, sponsorsForEvent }) => (
    <section
      className="pt-16 md:pt-20 pb-20 md:pb-24"
      data-testid="events-section"
    >
      <div className="container-x">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-10 md:mb-12 gap-5">
          <div>
            <p className="overline mb-3">The calendar</p>
            <h2 className="font-display font-light text-4xl md:text-5xl tracking-tight">
              Upcoming events
            </h2>
            <p className="text-white/60 mt-3 max-w-md text-sm">
              Virtual events. Pre-set master sessions. Small enough to remember
              names, structured enough to matter.
            </p>
          </div>
          <Link
            to="/events"
            className="btn-ghost text-sm self-start md:self-end"
            data-testid="events-all-cta"
          >
            View all events <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-7 md:gap-8">
          {loadingEvents ? (
            <>
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </>
          ) : sortedEvents.length === 0 ? (
            <p className="text-white/50">No events scheduled.</p>
          ) : (
            <>
              {sortedEvents.slice(0, 4).map((e) => (
                <EventCard
                  key={e.id}
                  event={e}
                  isBooked={e.isBooked}
                  tablesCount={e.actual_tables_count}
                  sponsors={(e?.sponcers || []).map((s) => ({
                    id: s?._id,
                    company: s?.title,
                    logo: s?.image,
                  }))}
                />
              ))}
              {sortedEvents.length > 4 && (
                <Link
                  to="/events"
                  className="group relative flex flex-col items-center justify-center rounded-3xl border border-dashed border-white/10 bg-white/[0.02] p-8 transition-all hover:bg-white/[0.04] hover:border-[#C6A87C]/40"
                >
                  <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-white/5 group-hover:bg-[#C6A87C]/10 transition-colors">
                    <Calendar className="text-[#C6A87C]" size={24} />
                  </div>
                  <h3 className="font-display text-xl tracking-tight text-white/90">
                    +{sortedEvents.length - 4} more events
                  </h3>
                  <p className="mt-2 text-sm text-white/50">
                    View the full curated calendar
                  </p>
                  <div className="mt-6 flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-[#C6A87C]">
                    View all <ArrowRight size={14} />
                  </div>
                </Link>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  ),
);
EventsSection.displayName = "EventsSection";

const SeekingGivingSection = memo(() => (
  <section
    className="section relative"
    data-testid="seeking-giving-section"
    style={{ background: "linear-gradient(180deg, #0A1D1A 0%, #061412 100%)" }}
  >
    <div className="container-x">
      <div className="grid md:grid-cols-12 gap-10 mb-10 items-end">
        <div className="md:col-span-7">
          <p className="overline mb-4 flex items-center gap-2">
            <Sparkles size={12} /> What's in the room
          </p>
          <h2 className="font-display font-light text-4xl md:text-5xl tracking-tight leading-tight">
            Seeking &amp; giving —<br />
            <span className="italic text-[#C6A87C]">
              live, before the doors open.
            </span>
          </h2>
        </div>
        <div className="md:col-span-5">
          <p className="text-white/75 leading-relaxed">
            Every member tells us what they're seeking and what they're
            offering. So when you walk into a room, you already see the trades
            that might happen.
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {SEEKING_GIVING_DATA.map((p, i) => (
          <article
            key={i}
            data-testid={`seeking-giving-card-${i}`}
            className="glass rounded-2xl p-5 flex flex-col gap-3 transition-all hover:-translate-y-0.5 hover:border-white/20"
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="font-display text-lg tracking-tight leading-tight">
                  {p.who}
                </h3>
                <p className="text-[11px] uppercase tracking-wider text-white/60">
                  {p.role}
                </p>
              </div>
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#C6A87C]/10 border border-[#C6A87C]/40 text-[#C6A87C] shrink-0">
                {p.topic}
              </span>
            </div>
            <div className="space-y-2.5">
              <div>
                <p className="overline text-[10px] mb-0.5 text-[#7BC8A6]">
                  ↗ Seeking
                </p>
                <p className="text-sm text-white/85 leading-snug">
                  {p.seeking}
                </p>
              </div>
              <div>
                <p className="overline text-[10px] mb-0.5 text-[#C6A87C]">
                  ↘ Giving
                </p>
                <p className="text-sm text-white/85 leading-snug">{p.giving}</p>
              </div>
            </div>
          </article>
        ))}
      </div>
      <p className="text-xs text-white/55 mt-6 leading-relaxed max-w-2xl">
        If an investor says they're seeking companies to back and you're a
        founder building in that space, the trade is already on the table.
        vmeetio is for people who are serious about their personal and business
        growth.
      </p>
    </div>
  </section>
));
SeekingGivingSection.displayName = "SeekingGivingSection";

const CirclesSection = memo(({ circles, cms }) => (
  <section
    className="section relative overflow-hidden"
    data-testid="circles-section"
    style={{ background: "linear-gradient(180deg, #061412 0%, #0E2623 100%)" }}
  >
    <div className="container-x">
      <div className="grid md:grid-cols-12 gap-10 mb-12 md:mb-14 items-end">
        <div className="md:col-span-7">
          <p className="overline mb-4">Trusted circles</p>
          <h2 className="font-display font-light text-4xl md:text-5xl tracking-tight leading-tight">
            {cms.circles_headline_1 || "Find the people you'd actually"}
            <br />
            <span className="italic text-[#C6A87C]">
              {cms.circles_headline_2 || "email at midnight."}
            </span>
          </h2>
        </div>
        <div className="md:col-span-5">
          <p className="text-white/75 leading-relaxed">
            Each circle is curated and stewarded. New members are introduced
            personally — you join the people who would back you, not a
            directory.
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {circles.map((c) => (
          <div
            key={c.name}
            data-testid={`circle-${c.name}`}
            className="glass rounded-2xl p-7 transition-all duration-300 hover:-translate-y-1 hover:border-white/20"
          >
            <div className="flex items-start justify-between mb-4">
              <h3 className="font-display text-2xl tracking-tight">{c.name}</h3>
              <span className="overline text-[#C6A87C]">
                {c.count.toLocaleString()}
              </span>
            </div>
            <p className="text-white/60 text-sm">{c.blurb}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
));
CirclesSection.displayName = "CirclesSection";

const ModeratorsSection = memo(({ moderators }) => (
  <section className="section" data-testid="moderators-section">
    <div className="container-x">
      <div className="grid md:grid-cols-12 gap-12 mb-12">
        <div className="md:col-span-5">
          <p className="overline mb-4">For conversations, you need a steward</p>
          <h2 className="font-display font-light text-4xl md:text-5xl tracking-tight leading-tight">
            Add your own moderator —<br />
            <span className="italic text-[#C6A87C]">
              or choose from our approved global experts.
            </span>
          </h2>
        </div>
        <div className="md:col-span-6 md:col-start-7">
          <p className="text-white/75 text-lg leading-relaxed mb-5">
            Every table is anchored by a moderator — an operator, researcher or
            practitioner who has lived the topic. They don't lecture. They keep
            the conversation honest, pull quieter voices forward, and let the
            conversation breathe.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/host"
              className="btn-gold text-sm"
              data-testid="moderator-byo-cta"
            >
              Bring your own moderator <ArrowRight size={14} />
            </Link>
            <Link
              to="/moderators/apply"
              className="btn-ghost text-sm"
              data-testid="moderator-marketplace-cta"
            >
              Browse approved moderators <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
        {moderators.map((m) => (
          <div
            key={m.name}
            className="glass rounded-2xl overflow-hidden group transition-all duration-300 hover:-translate-y-1 hover:border-white/20"
            data-testid={`moderator-${m.name}`}
          >
            <div className="aspect-[4/5] overflow-hidden">
              <img
                src={m.avatar}
                alt={m.name}
                loading="lazy"
                decoding="async"
                width={400}
                height={500}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </div>
            <div className="p-5">
              <h3 className="font-display text-xl tracking-tight">{m.name}</h3>
              <p className="overline mt-2">{m.title}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
));
ModeratorsSection.displayName = "ModeratorsSection";

const SponsorStripSection = memo(({ cms }) => {
  const tiers =
    cms.sponsor_tiers && cms.sponsor_tiers.length
      ? cms.sponsor_tiers
      : [
          {
            id: "table",
            name: "Table Host",
            price: 8400,
            currency: "USD",
            cadence: "",
            tagline: "One curated table per event",
          },
          {
            id: "circle",
            name: "Circle Patron",
            price: 24000,
            currency: "USD",
            cadence: "",
            tagline: "Sponsor a full circle for a season",
          },
          {
            id: "founding",
            name: "Founding Partner",
            price: 0,
            currency: "",
            cadence: "Bespoke",
            tagline: "Year-round programming + co-branding",
          },
        ];

  return (
    <section
      className="section"
      data-testid="sponsor-strip-section"
      style={{
        background: "linear-gradient(180deg, #061412 0%, #0A1D1A 100%)",
      }}
    >
      <div className="container-x">
        <div className="glass rounded-3xl p-10 md:p-16 relative overflow-hidden">
          <div
            className="absolute -top-32 -right-32 w-96 h-96 rounded-full"
            style={{
              background:
                "radial-gradient(circle, rgba(198,168,124,0.18), transparent 70%)",
            }}
          />
          <div className="grid md:grid-cols-12 gap-12 relative">
            <div className="md:col-span-7">
              <p className="overline mb-5 flex items-center gap-2">
                <Sparkles size={12} /> Sponsor a table
              </p>
              <h2 className="font-display font-light text-4xl md:text-5xl tracking-tight mb-6 leading-tight">
                Become the host of the conversation your category needs.
              </h2>
              <p className="text-white/70 text-base leading-relaxed mb-8 max-w-xl">
                vmeetio partners place your team at the head of a curated table.
                Choose your topic, choose your moderator, and we'll fill the
                seats with the people you'd otherwise spend a quarter trying to
                reach.
              </p>
              <Link
                to="/sponsor"
                className="btn-gold"
                data-testid="sponsor-cta"
              >
                Become a sponsor <ArrowRight size={16} />
              </Link>
            </div>
            <div className="md:col-span-5">
              <div className="grid gap-4" data-testid="sponsor-tier-grid">
                {tiers.map((tier) => {
                  const priceLabel =
                    tier.price && Number(tier.price) > 0
                      ? `${
                          tier.currency === "USD" || !tier.currency
                            ? "$"
                            : tier.currency + " "
                        }${Number(tier.price).toLocaleString()}`
                      : tier.cadence || "Bespoke";
                  return (
                    <div
                      key={tier.id || tier.name}
                      data-testid={`sponsor-tier-${tier.id || tier.name}`}
                      className="border border-white/[0.08] rounded-xl p-5 flex items-center justify-between hover:border-[#C6A87C]/40 transition-colors gap-4"
                    >
                      <div className="min-w-0">
                        <p className="font-medium">{tier.name}</p>
                        <p className="text-xs text-white/55 mt-1">
                          {tier.tagline}
                        </p>
                        {tier.cadence && Number(tier.price) > 0 && (
                          <p className="text-[10px] uppercase tracking-wider text-white/45 mt-1">
                            {tier.cadence}
                          </p>
                        )}
                      </div>
                      <p className="font-display text-2xl text-[#C6A87C] shrink-0">
                        {priceLabel}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
});
SponsorStripSection.displayName = "SponsorStripSection";

const PartnerShowcaseSection = memo(({ brands }) => (
  <section className="section" data-testid="partner-showcase-section">
    <div className="container-x">
      <div className="grid md:grid-cols-12 gap-12 mb-14 items-end">
        <div className="md:col-span-7">
          <p className="overline mb-4">Partner showcase</p>
          <h2 className="font-display font-light text-4xl md:text-5xl tracking-tight leading-tight">
            The brands hosting our tables.
          </h2>
        </div>
        <div className="md:col-span-5">
          <p className="text-white/75 leading-relaxed">
            Each partner brings a point of view to the table. Here's what
            they're building.
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {brands.map((b) => (
          <Link
            key={b._id}
            to={`/brands/${encodeURIComponent(b.title)}`}
            state={{ sponsorData: b }}
            data-testid={`partner-${b.title}`}
            className="glass rounded-2xl p-7 group transition-all duration-300 hover:-translate-y-1 hover:border-white/20 block"
          >
            <p className="font-display text-2xl tracking-tight mb-2">
              {b.title}
            </p>
            <div
              className="text-sm text-white/60 leading-relaxed mb-5 line-clamp-3"
              dangerouslySetInnerHTML={{ __html: b.descp || "—" }}
            />
            <span className="text-xs text-[#C6A87C] flex items-center gap-1">
              View Profile <ArrowRight size={12} />
            </span>
          </Link>
        ))}
      </div>
    </div>
  </section>
));
PartnerShowcaseSection.displayName = "PartnerShowcaseSection";

const CuratedContentSection = memo(() => (
  <section className="section" data-testid="curated-content-section">
    <div className="container-x grid md:grid-cols-12 gap-12">
      <div className="md:col-span-5">
        <p className="overline mb-4">Curated content</p>
        <h2 className="font-display font-light text-4xl md:text-5xl tracking-tight mb-6 leading-tight">
          After the table, the dossier.
        </h2>
        <p className="text-white/70 leading-relaxed">
          Every vmeetio event closes with a dossier — written notes, soft
          introductions, and a curated reading list from the moderator. The
          conversation lives on past the room.
        </p>
        <ul className="mt-8 space-y-3 text-white/70">
          {[
            "Moderator's notes from each table",
            "Soft intros to the 3 people you'd want to meet",
            "A weekly newsletter from your circles",
          ].map((x) => (
            <li key={x} className="flex items-start gap-3">
              <CheckCircle2 size={16} className="mt-0.5 text-[#C6A87C]" />
              <span className="text-sm">{x}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="md:col-span-6 md:col-start-7">
        <div className="glass rounded-2xl p-8">
          <p className="overline mb-6">
            Dossier · The Founders' Round · Vol. 12
          </p>
          <h3 className="font-display text-3xl tracking-tight italic text-white/90 mb-6 leading-snug">
            "The bridge round is the new Series A. Founders are pricing flat,
            optionality is the new leverage, and the board conversation has
            fundamentally shifted."
          </h3>
          <p className="overline">— Eliza Marston, moderating Table 3</p>
          <div className="border-t border-white/[0.06] mt-8 pt-6">
            <p className="overline mb-3">Reading list</p>
            <ul className="space-y-2 text-sm text-white/70">
              {DOSSIER_READING_LIST.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  </section>
));
CuratedContentSection.displayName = "CuratedContentSection";

const MembershipCTASection = memo(() => (
  <section
    className="section relative overflow-hidden"
    data-testid="membership-cta-section"
    style={{ background: "linear-gradient(180deg, #061412 0%, #0A1D1A 100%)" }}
  >
    <div className="container-x">
      <div className="glass rounded-3xl p-10 md:p-16 relative overflow-hidden">
        <div
          className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full"
          style={{
            background:
              "radial-gradient(circle, rgba(198,168,124,0.15), transparent 70%)",
          }}
        />
        <div className="grid md:grid-cols-12 gap-12 relative items-center">
          <div className="md:col-span-7">
            <p className="overline mb-5 flex items-center gap-2">
              <Crown size={12} /> Exclusive membership
            </p>
            <h2 className="font-display font-light text-4xl md:text-5xl tracking-tight mb-6 leading-tight">
              Reserved seats at VIP tables —<br />
              <span className="italic text-[#C6A87C]">
                capped at six guests.
              </span>
            </h2>
            <p className="text-white/70 leading-relaxed mb-8 max-w-xl">
              vmeetio members get early access to the calendar, priority
              moderator matching, and reserved seats at VIP-only tables — six
              guests per room, by introduction.
            </p>
            <Link
              to="/network#apply-membership"
              className="btn-gold"
              data-testid="membership-cta"
            >
              See membership tiers <ArrowRight size={16} />
            </Link>
          </div>
          <div className="md:col-span-5">
            <div className="space-y-3">
              {MEMBERSHIP_TIERS.map(([label, price]) => (
                <div
                  key={label}
                  className="border border-white/[0.08] rounded-xl p-4 flex items-center justify-between hover:border-[#C6A87C]/40 transition-colors"
                >
                  <p className="font-medium">{label}</p>
                  <p className="text-sm text-[#C6A87C]">{price}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
));
MembershipCTASection.displayName = "MembershipCTASection";

const FAQSection = memo(({ faq, openFaq, setOpenFaq }) => (
  <section className="section" data-testid="faq-section">
    <div className="container-x max-w-4xl">
      <div className="mb-12">
        <p className="overline mb-4">Questions, answered.</p>
        <h2 className="font-display font-light text-4xl md:text-5xl tracking-tight leading-tight">
          A few things{" "}
          <span className="italic text-[#C6A87C]">
            people ask before joining.
          </span>
        </h2>
      </div>
      <div className="space-y-2">
        {faq.map((item, i) => {
          const isOpen = openFaq === i;
          return (
            <div
              key={i}
              data-testid={`faq-item-${i}`}
              className={`border rounded-2xl transition-all ${
                isOpen
                  ? "border-[#C6A87C]/45 bg-[#C6A87C]/[0.04]"
                  : "border-white/[0.08]"
              }`}
            >
              <button
                type="button"
                onClick={() => setOpenFaq(isOpen ? -1 : i)}
                data-testid={`faq-toggle-${i}`}
                aria-expanded={isOpen}
                className="w-full flex items-center justify-between gap-6 px-6 py-5 text-left hover:text-[#C6A87C] transition-colors"
              >
                <span className="font-display text-lg md:text-xl tracking-tight leading-snug">
                  {item.q}
                </span>
                <ChevronDown
                  size={18}
                  className={`shrink-0 transition-transform ${
                    isOpen ? "rotate-180 text-[#C6A87C]" : "text-white/55"
                  }`}
                />
              </button>
              {isOpen && (
                <div
                  className="px-6 pb-6 text-sm text-white/75 leading-relaxed"
                  data-testid={`faq-answer-${i}`}
                >
                  {item.a}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  </section>
));
FAQSection.displayName = "FAQSection";

const WaitlistSection = memo(({ submitted, email, setEmail, onSubmit }) => (
  <section
    className="section relative overflow-hidden"
    data-testid="waitlist-section"
  >
    <div className="container-x">
      <div className="max-w-2xl">
        <p className="overline mb-5">Request access</p>
        <h2 className="font-display font-light text-4xl md:text-6xl tracking-tighter leading-[0.95] mb-8">
          Membership is by introduction.
          <br />
          <span className="italic text-[#C6A87C]">
            Tell us where you'd sit.
          </span>
        </h2>
        <p className="text-white/75 leading-relaxed mb-10 max-w-lg">
          vmeetio is members-led. We'll send you the next open event and ask one
          question about the circle you'd like to join.
        </p>
        {submitted ? (
          <div
            className="glass rounded-2xl p-6 flex items-center gap-3"
            data-testid="waitlist-success"
          >
            <CheckCircle2 className="text-[#C6A87C]" /> You're on the list.
            Watch your inbox.
          </div>
        ) : (
          <form
            onSubmit={onSubmit}
            className="flex flex-col sm:flex-row gap-3"
            data-testid="waitlist-form"
          >
            <input
              required
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              data-testid="waitlist-email-input"
              className="flex-1 bg-white/[0.04] border border-white/[0.1] rounded-full px-6 py-4 placeholder:text-white/35 focus:border-[#C6A87C] transition-colors"
            />
            <button
              type="submit"
              data-testid="waitlist-submit"
              className="btn-gold"
            >
              Request access <ArrowRight size={16} />
            </button>
          </form>
        )}
      </div>
    </div>
  </section>
));
WaitlistSection.displayName = "WaitlistSection";

// ---------------------------------------------------------------------------
// HERO LIVE TABLE CAROUSEL — memoised
// ---------------------------------------------------------------------------
const HeroLiveTableCarousel = memo(function HeroLiveTableCarousel({
  tables,
  events,
  sponsorsForTable,
}) {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (idx >= tables.length) setIdx(0);
  }, [tables.length, idx]);

  useEffect(() => {
    if (paused || tables.length <= 1) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % tables.length), 6000);
    return () => clearInterval(id);
  }, [paused, tables.length]);

  const go = useCallback(
    (n) => setIdx(((n % tables.length) + tables.length) % tables.length),
    [tables.length],
  );

  if (tables.length === 0) return null;

  const t = tables[idx] || tables[0];
  const ev = events.find((e) => e.id === t.event_id) || null;

  return (
    <div
      data-testid="hero-table-carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <div className="flex items-center justify-between mb-3">
        <p className="overline flex items-center gap-2 text-[11px]">
          <Mic2 size={13} /> Live at a table now
        </p>
        {tables.length > 1 && (
          <div
            className="flex items-center gap-1.5"
            data-testid="hero-carousel-controls"
          >
            <button
              type="button"
              onClick={() => go(idx - 1)}
              data-testid="hero-carousel-prev"
              aria-label="Previous live table"
              className="w-8 h-8 rounded-full border border-white/15 text-white/65 hover:text-[#C6A87C] hover:border-[#C6A87C]/45 transition-colors inline-flex items-center justify-center"
            >
              <ArrowRight size={14} className="rotate-180" />
            </button>
            <span
              className="text-xs tracking-wider text-white/65 mx-2 font-mono-acc"
              data-testid="hero-carousel-idx"
            >
              {String(idx + 1).padStart(2, "0")} /{" "}
              {String(tables.length).padStart(2, "0")}
            </span>
            <button
              type="button"
              onClick={() => go(idx + 1)}
              data-testid="hero-carousel-next"
              aria-label="Next live table"
              className="w-8 h-8 rounded-full border border-white/15 text-white/65 hover:text-[#C6A87C] hover:border-[#C6A87C]/45 transition-colors inline-flex items-center justify-center"
            >
              <ArrowRight size={14} />
            </button>
          </div>
        )}
      </div>

      <div className="h-full">
        <TableCard
          key={t.id}
          table={t}
          event={ev}
          sponsors={sponsorsForTable(t.id)}
          onViewAgenda={() => {
            console.log("View agenda for table:", t.id);
          }}
          onJoin={(data) => {
            console.log("Join table:", data);
          }}
          onAttendeeClick={(name) => {
            console.log("Attendee clicked:", name);
          }}
        />
      </div>

      {tables.length > 1 && (
        <div
          className="mt-4 flex items-center justify-center gap-1.5 h-2"
          data-testid="hero-carousel-dots"
        >
          {tables.map((tt, i) => {
            const distance = Math.abs(i - idx);
            if (distance > 4) return null;

            let size = "w-1.5 h-1.5";
            let opacity = "bg-white/20";

            if (i === idx) {
              size = "w-6 h-1.5";
              opacity = "bg-[#C6A87C]";
            } else if (distance === 1) {
              size = "w-1.5 h-1.5";
              opacity = "bg-white/60";
            } else if (distance === 2) {
              size = "w-1.2 h-1.2";
              opacity = "bg-white/30";
            } else if (distance === 3) {
              size = "w-1 h-1";
              opacity = "bg-white/15";
            } else if (distance === 4) {
              size = "w-0.5 h-0.5";
              opacity = "bg-white/5";
            }

            return (
              <button
                key={tt.id}
                type="button"
                onClick={() => go(i)}
                aria-label={`Show live table ${i + 1}`}
                data-testid={`hero-carousel-dot-${i}`}
                className={`${size} rounded-full transition-all duration-500 ease-in-out ${opacity} hover:bg-[#C6A87C]/50`}
              />
            );
          })}
        </div>
      )}
    </div>
  );
});

// ---------------------------------------------------------------------------
// MAIN LANDING PAGE
// ---------------------------------------------------------------------------
export default function Landing() {
  const [circles] = useState(STATIC_CIRCLES);
  const [moderators] = useState(STATIC_MODERATORS);
  const [sponsorships] = useState(STATIC_SPONSORSHIPS);
  const [content] = useState(STATIC_CONTENT);

  // Remote data
  const [brands, setBrands] = useState([]);
  const [loadingBrands, setLoadingBrands] = useState(true);
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [tables, setTables] = useState([]);
  const [loadingTables, setLoadingTables] = useState(true);
  const [liveTableCount, setLiveTableCount] = useState(0);

  // UI state
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [openEvent, setOpenEvent] = useState(null);
  const [openFaq, setOpenFaq] = useState(0);
  const [activeQuote, setActiveQuote] = useState(0);

  const cms = content;

  const TESTIMONIALS = useMemo(
    () =>
      Array.isArray(cms.testimonials) && cms.testimonials.length
        ? cms.testimonials
        : FALLBACK_TESTIMONIALS,
    [cms.testimonials],
  );

  const FAQ = useMemo(
    () => (Array.isArray(cms.faq) && cms.faq.length ? cms.faq : FALLBACK_FAQ),
    [cms.faq],
  );

  const HOW_IT_WORKS = useMemo(
    () =>
      Array.isArray(cms.how_it_works) && cms.how_it_works.length
        ? cms.how_it_works
        : FALLBACK_HOW_IT_WORKS,
    [cms.how_it_works],
  );

  // Testimonial auto-advance
  useEffect(() => {
    const t = setInterval(
      () => setActiveQuote((i) => (i + 1) % TESTIMONIALS.length),
      7000,
    );
    return () => clearInterval(t);
  }, [TESTIMONIALS.length]);

  // ---------------------------------------------------------------------------
  // SINGLE consolidated data fetch — replaces the 4 overlapping useEffects
  // ---------------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;

    const fetchAll = async () => {
      try {
        // Fire all three top-level requests in parallel
        const [partRes, evRes, myRes] = await Promise.allSettled([
          getAllPartners(),
          getAllEvents(),
          getMyEvents(),
        ]);

        if (cancelled) return;

        // --- Partners ---
        if (
          partRes.status === "fulfilled" &&
          partRes.value?.success &&
          Array.isArray(partRes.value.data)
        ) {
          setBrands(partRes.value.data);
        }
        setLoadingBrands(false);

        // --- Events + per-event table counts ---
        const rawEvents =
          evRes.status === "fulfilled"
            ? evRes.value?.data?.data || evRes.value?.data || []
            : [];

        const bookings =
          myRes.status === "fulfilled" ? myRes.value?.data || [] : [];

        const bookedSet = new Set(bookings.map((b) => b.newsId));

        if (!Array.isArray(rawEvents) || rawEvents.length === 0) {
          setLoadingEvents(false);
          return;
        }

        // Fetch per-session table counts for every event concurrently,
        // but cap parallelism so we don't hammer the server with 100s of requests.
        const BATCH = 5; // process 5 events at a time
        const normalized = [];

        for (let i = 0; i < rawEvents.length; i += BATCH) {
          if (cancelled) return;
          const batch = rawEvents.slice(i, i + BATCH);

          const results = await Promise.all(
            batch.map(async (e) => {
              const base = normalizeEventData(e);
              let totalTables = 0;

              if (Array.isArray(e.sessions) && e.sessions.length > 0) {
                const tableResponses = await Promise.allSettled(
                  e.sessions.map((s) => getSessionTables(s._id)),
                );
                totalTables = tableResponses.reduce(
                  (sum, r) =>
                    sum +
                    (r.status === "fulfilled"
                      ? r.value?.data?.tables?.length || 0
                      : 0),
                  0,
                );
              }

              return {
                ...base,
                isBooked: bookedSet.has(e._id),
                actual_tables_count: totalTables,
              };
            }),
          );

          normalized.push(...results);

          // Progressive render: update state after each batch
          if (!cancelled) {
            setEvents([...normalized]);
          }
        }
      } catch (err) {
        console.error("Landing data fetch error:", err);
      } finally {
        if (!cancelled) setLoadingEvents(false);
      }
    };

    fetchAll();
    return () => {
      cancelled = true;
    };
  }, []);

  // ---------------------------------------------------------------------------
  // Live tables — poll every 45 s
  // ---------------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;

    const fetchLiveTables = async () => {
      try {
        const res = await getAllEventSessionsTables();
        if (cancelled) return;
        if (res?.success && Array.isArray(res.data)) {
          setTables(flattenAndNormalizeTables(res.data));
          setLiveTableCount(res.count ?? res.data.length);
        }
      } catch (err) {
        console.error("Failed to fetch live tables", err);
      } finally {
        if (!cancelled) setLoadingTables(false);
      }
    };

    fetchLiveTables();
    const interval = setInterval(fetchLiveTables, 45_000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  // ---------------------------------------------------------------------------
  // Derived / memoised values
  // ---------------------------------------------------------------------------
  const sortedEvents = useMemo(() => {
    const now = Date.now();
    return [...events].sort((a, b) => {
      const aStart = new Date(a.start_time).getTime();
      const bStart = new Date(b.start_time).getTime();
      const aEnd = new Date(a.end_time).getTime();
      const bEnd = new Date(b.end_time).getTime();
      const aIsPast = now > aEnd;
      const bIsPast = now > bEnd;
      if (aIsPast && !bIsPast) return 1;
      if (!aIsPast && bIsPast) return -1;
      if (!aIsPast && !bIsPast) return aStart - bStart;
      return bEnd - aEnd;
    });
  }, [events]);

  // Stable callback refs — prevents child re-renders
  const sponsorsForEvent = useCallback(
    (id) =>
      sponsorships.filter((s) => s.event_id === id && s.scope === "event"),
    [sponsorships],
  );

  const sponsorsForTable = useCallback(
    (id) =>
      sponsorships.filter((s) => s.scope === "table" && s.table_id === id),
    [sponsorships],
  );

  const submitWaitlist = useCallback(async (e) => {
    e.preventDefault();
    setSubmitted(true);
    toast.success("You're on the list. We'll be in touch.");
  }, []);

  const heroBg = cms.hero_image || DEFAULT_HERO_BG;

  return (
    <div data-testid="landing-page">
      {/* ------------------------------------------------------------------ */}
      {/* HERO                                                                */}
      {/* ------------------------------------------------------------------ */}
      <section
        className="relative overflow-hidden grain"
        data-testid="hero-section"
      >
        {/* Background — preload hint injected in <head> separately */}
        <div className="absolute inset-0 -z-10">
          <img
            src={heroBg}
            alt=""
            // LCP image: eager + high fetchpriority
            loading="eager"
            fetchPriority="high"
            decoding="async"
            className="w-full h-full object-cover opacity-[0.18]"
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(ellipse at 30% 20%, rgba(42,126,93,0.18), transparent 60%), linear-gradient(180deg, #061412 0%, #061412 100%)",
            }}
          />
        </div>

        <div className="container-x pt-16 md:pt-20 pb-12 md:pb-16">
          <div className="grid md:grid-cols-12 gap-10 md:gap-12 items-start">
            {/* LEFT */}
            <div className="md:col-span-7">
              <p
                className="overline mb-5 flex items-center gap-3"
                data-testid="hero-overline"
              >
                <span className="w-6 h-px bg-[#C6A87C]" />
                {cms.hero_overline ||
                  "A members-led society for virtual events"}
              </p>
              <h1 className="font-display font-light text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] mb-6">
                {cms.hero_headline_1 || "Conversations"}
                <br />
                <span className="italic text-[#C6A87C]">
                  {cms.hero_headline_2 || "worth showing up for."}
                </span>
              </h1>
              <p className="text-white/85 text-lg md:text-xl max-w-xl leading-relaxed mb-8">
                {cms.hero_body ||
                  "vmeetio runs virtual events as a set of master sessions and small, curated tables. Move between topics. Build a trusted circle. Meet the people who can elevate your growth fastest."}
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to="/lobby"
                  data-testid="hero-cta-primary"
                  className="btn-gold"
                >
                  Enter the lobby <ArrowRight size={16} />
                </Link>
                <Link
                  to="/host"
                  data-testid="hero-cta-secondary"
                  className="btn-ghost"
                >
                  Host an event <ArrowUpRight size={16} />
                </Link>
              </div>

              <HeroTrustedStrip brands={brands} />
              <HeroValueStrip strip={cms.value_strip || []} />
              <HeroQuote
                text={cms.hero_quote_text}
                attribution={cms.hero_quote_attribution}
              />
            </div>

            {/* RIGHT — live tables */}
            <div className="md:col-span-5" data-testid="hero-right">
              {tables.length > 0 ? (
                <div data-testid="hero-table-preview">
                  <HeroLiveScoreboard
                    tables={tables}
                    circles={circles}
                    loadingTables={loadingTables}
                    liveTableCount={liveTableCount}
                  />
                  <HeroLiveTableCarousel
                    tables={tables}
                    events={events}
                    sponsorsForTable={sponsorsForTable}
                  />
                </div>
              ) : (
                <div className="glass rounded-2xl p-7">
                  <p className="overline mb-3">Tonight in the lobby</p>
                  <p className="text-4xl font-display tracking-tight">
                    <span className="text-[#C6A87C]">
                      {loadingTables ? "..." : 0}
                    </span>{" "}
                    tables live
                  </p>
                  <p className="text-base text-white/75 mt-2">
                    Across {circles.length || "..."} circles.
                  </p>
                </div>
              )}
            </div>
          </div>

          <OutcomeStrip />
        </div>
      </section>

      {/* Lazy-loaded below-fold panels — each wrapped in Suspense so they
          stream in independently without blocking the hero paint           */}
      <Suspense fallback={null}>
        <LiveNowPanel />
      </Suspense>

      <Suspense fallback={null}>
        <TrendingCirclesStrip limit={4} />
      </Suspense>

      <TestimonialSection
        testimonials={TESTIMONIALS}
        activeQuote={activeQuote}
        setActiveQuote={setActiveQuote}
      />

      <Suspense fallback={null}>
        <TodayTomorrow />
      </Suspense>

      <BrandsSection brands={brands} loadingBrands={loadingBrands} cms={cms} />

      <MethodSection cms={cms} />

      <HowItWorksSection steps={HOW_IT_WORKS} />

      <LobbyPreviewSection
        tables={tables}
        events={events}
        loadingTables={loadingTables}
        sponsorsForTable={sponsorsForTable}
      />

      <EventsSection
        sortedEvents={sortedEvents}
        loadingEvents={loadingEvents}
        sponsorsForEvent={sponsorsForEvent}
      />

      <SeekingGivingSection />

      <CirclesSection circles={circles} cms={cms} />

      <ModeratorsSection moderators={moderators} />

      <SponsorStripSection cms={cms} />

      <PartnerShowcaseSection brands={brands} />

      <CuratedContentSection />

      <MembershipCTASection />

      {FAQ.length > 0 && (
        <FAQSection faq={FAQ} openFaq={openFaq} setOpenFaq={setOpenFaq} />
      )}

      <WaitlistSection
        submitted={submitted}
        email={email}
        setEmail={setEmail}
        onSubmit={submitWaitlist}
      />

      <Suspense fallback={null}>
        {openEvent && (
          <EventRegisterModal
            event={openEvent}
            open={!!openEvent}
            onClose={() => setOpenEvent(null)}
          />
        )}
      </Suspense>
    </div>
  );
}
